import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Box, Button } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import { AuthLayout } from "../../../components/layout";
import { Form } from "../../../components/Form";
import FormTextInput from "../../../components/Form/FormTextInput";
import FormFieldErrors from "../../../components/Form/FormFieldErrors";
import {
  useFormContext,
  FormDataType,
  FormFieldValue,
} from "../../../components/Form/FormContext";
import FormDataSaver from "../../../components/FormDataSaver";
import { selectAuthLoading } from "../../../store/selectors/authSelectors";
import { login as loginAction } from "../../../store/actions/authActions";
import { ROUTES, STORAGE_KEYS } from "../../../constants";
import { validatePassword } from "../../../utils/passwordValidation";
import { useApiErrorHandling } from "../../../hooks";
import { ValidationResult } from "../../../hooks/useFormValidation";
import {
  LoginPageContainer,
  LoginFormContainer,
  LoginTitle,
  LoginSubtitle,
} from "./LoginPage.styled";

interface LoginFormProps {
  apiErrors: string[];
  fieldErrors: { [k: string]: string[] };
}

const LoginForm: React.FC<LoginFormProps> = ({ apiErrors, fieldErrors }) => {
  const { t } = useTranslation();
  const isLoading = useSelector(selectAuthLoading);
  const { validationResult } = useFormContext();

  // Disable submit when client-side validation reports errors
  const isFormValid =
    !!validationResult?.isValid &&
    Array.isArray(validationResult?.errorMessages) &&
    validationResult.errorMessages.length === 0;

  return (
    <>
      <FormTextInput
        name="email"
        label={t("login.email")}
        type="email"
        placeholder={t("login.emailPlaceholder")}
        required
      />

      <FormTextInput
        name="password"
        label={t("login.password")}
        type="password"
        placeholder={t("login.passwordPlaceholder")}
        required
      />

      <Box sx={{ mt: "14px" }}>
        <Link to={ROUTES.FORGOT_PASSWORD}>{t("login.forgotPassword")}</Link>
      </Box>

      <FormFieldErrors apiErrors={apiErrors} fieldErrors={fieldErrors} />

      <Box sx={{ mt: "32px" }}>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={isLoading || !isFormValid}
        >
          {isLoading ? "Logging in..." : t("login.loginButton")}
        </Button>
      </Box>
    </>
  );
};

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { apiErrors, fieldErrors, clearErrors, handleApiError } =
    useApiErrorHandling({
      defaultErrorMessage: "Incorrect email or password. Please try again.",
    });
  const [savedFormData, setSavedFormData] = useState<FormDataType | null>(null);

  // Load saved form data on component mount
  useEffect(() => {
    const savedData = sessionStorage.getItem(STORAGE_KEYS.LOGIN_CREDENTIALS);
    if (savedData) {
      try {
        setSavedFormData(JSON.parse(savedData));
      } catch (error) {
        console.error("Failed to parse saved login data:", error);
      }
    }
  }, []);

  const validationRules = useMemo(
    () => ({
      email: {
        required: true,
        pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: "Please enter a valid email address.",
      },
      password: {
        required: true,
        // Align password validation with registration page
        custom: (value: FormFieldValue) => {
          const strValue = String(value || "");
          if (!strValue) {
            return t("register.validation.passwordRequired");
          }
          const error = validatePassword(strValue, t);
          return error ? t("register.validation.passwordMinLength") : null;
        },
      },
    }),
    [t]
  );

  const handleSubmit = useCallback(
    async (formData: FormDataType, validationResult: ValidationResult) => {
      // Block submit strictly based on validationRules outcome
      const hasClientErrors =
        !validationResult?.isValid ||
        (Array.isArray(validationResult?.errorMessages) &&
          validationResult.errorMessages.length > 0);
      if (hasClientErrors) return;

      clearErrors(); // Clear previous errors
      try {
        await dispatch(
          loginAction({
            email: String(formData.email),
            password: String(formData.password),
          })
        ).unwrap();

        // Save credentials for 2FA verification
        sessionStorage.setItem(
          STORAGE_KEYS.LOGIN_CREDENTIALS,
          JSON.stringify({
            email: String(formData.email),
            password: String(formData.password),
          })
        );
        sessionStorage.setItem(STORAGE_KEYS.EMAIL, String(formData.email));

        // Navigate to verification page
        navigate(`${ROUTES.VERIFICATION}?redirect=${ROUTES.TWO_FACTOR_AUTH}`);
      } catch (errors: unknown) {
        handleApiError(errors);
      }
    },
    [dispatch, navigate, clearErrors, handleApiError]
  );

  return (
    <AuthLayout>
      <LoginPageContainer>
        <LoginFormContainer>
          <LoginTitle>{t("login.title")}</LoginTitle>

          <LoginSubtitle>{t("login.subtitle")}</LoginSubtitle>

          <Form
            validationRules={validationRules}
            onSubmit={handleSubmit}
            initialData={savedFormData || undefined}
            externalErrors={fieldErrors}
            showFieldErrors={false}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              width: "100%",
            }}
          >
            <FormDataSaver storageKey={STORAGE_KEYS.LOGIN_CREDENTIALS} />
            <LoginForm apiErrors={apiErrors} fieldErrors={fieldErrors} />
          </Form>
        </LoginFormContainer>
      </LoginPageContainer>
    </AuthLayout>
  );
};

export default LoginPage;
